/**
 * Office locations: an admin-managed master list (Admin > Settings > Office
 * Locations) that feeds the Office Location dropdown of Email Creation
 * requests. Locations are never deleted (historical tickets reference them);
 * they are deactivated, which hides them from NEW requests only.
 */
import { z } from 'zod';
import { pagedQueryDtoSchema } from './admin-dto.js';
export const OFFICE_LOCATION_CODE_MAX = 30;
/** Trimmed, upper-cased on the way in, so "bngblr" and "BNGBLR" are the same code. */
export const officeLocationCodeSchema = z
    .string()
    .transform((v) => v.trim().toUpperCase())
    .pipe(z
    .string()
    .min(1, 'Office Location Code is required.')
    .max(OFFICE_LOCATION_CODE_MAX, `Use ${OFFICE_LOCATION_CODE_MAX} characters or fewer.`)
    .regex(/^[A-Z0-9][A-Z0-9 _.\-/]*$/, 'Use letters, numbers, spaces, dots, dashes, slashes or underscores only.'));
/** Optional descriptive text: trimmed, blank becomes null. */
const optionalText = (max) => z
    .string()
    .max(max * 2) // generous pre-trim bound; the real limit is applied after trimming
    .nullish()
    .transform((v) => (v == null ? null : v.trim() === '' ? null : v.trim()))
    .pipe(z.string().max(max, `Use ${max} characters or fewer.`).nullable());
export const adminCreateOfficeLocationDtoSchema = z
    .object({
    code: officeLocationCodeSchema,
    name: optionalText(150),
    city: optionalText(100),
    stateRegion: optionalText(100),
    country: optionalText(100),
    isActive: z.boolean().default(true),
})
    .strict();
/** PATCH: only the fields sent are changed (an explicit null/blank clears an optional field). */
export const adminUpdateOfficeLocationDtoSchema = z
    .object({
    code: officeLocationCodeSchema.optional(),
    name: optionalText(150).optional(),
    city: optionalText(100).optional(),
    stateRegion: optionalText(100).optional(),
    country: optionalText(100).optional(),
    isActive: z.boolean().optional(),
})
    .strict()
    .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update.' });
export const OFFICE_LOCATION_SORTS = ['code', 'name', 'city', 'updatedAt'];
export const adminOfficeLocationListQueryDtoSchema = pagedQueryDtoSchema.extend({
    status: z.enum(['active', 'inactive']).optional(),
    sort: z.enum(OFFICE_LOCATION_SORTS).default('code'),
    dir: z.enum(['asc', 'desc']).default('asc'),
});
//# sourceMappingURL=office-location-dto.js.map